<template lang="pug">
div
  component(
    ref="component"
    :is="blueprint"
    :entity="entity"
    :auto-refresh="autoRefresh"
    :busy="busy"
    :previousRoute="previousRoute"
    v-bind="$props"
    @refresh="opts => restore(opts)"
    @setAutoRefresh="setAutoRefresh"
  )
</template>

<script>
import { ENTITY, STORE_ENTITY } from '@/store/mutation-types'
import { canonicalizeLink } from '@/helpers/canonicalization'
import { mapState } from 'vuex'
import { isCompatibleEref } from '@/helpers/erefs/'

import blueprints from "@/components/blueprints"

export default {
  props: {
    site: String,
    eref: String,
    noFetch: Boolean,

    /**
     * Coleção de dicas de relacionamento.
     * Aplicável quando a rota é aberta por um link.
     * Esta propriedade carrega as dicas de relacionamento do link.
     */
    rel: Array,
    /**
     * Coleção de regras de abertura da rota.
     * Aplicável quando a rota é aberta por um link.
     */
    rule: Array,

    // Função acionada logo após o download da entidade.
    callback: Function,
  },

  components: {
    ...blueprints
  },

  data: () => ({
    // {Keep.Paper.Design.Link}
    previousRoute: null,
    // Diz se a sincronizacao de dados está em andamento.
    busy: false,
    // Data calculada da próxima sincronização de dados automática.
    autoRestoreDate: null,
  }),

  computed: {
    ...mapState({
      entity (state) {
        return state[ENTITY][this.entityLink.path]
      },
    }),

    entityLink () {
      return canonicalizeLink(this, this.$route.params)
    },

    type() {
      return this.entity?.type
    },

    blueprint() {
      if (this.type === 'Status') return 'Status'
      if (!this.entity) return 'Loading'

      const { blueprint } = this.entity
      let name = !blueprint
        ? 'Status'
        : blueprint?.see || blueprint?.eref || blueprint?.name;

      // NOTE: Blueprints redirecionados...
      if (name === 'Provider') name = 'List'
      if (name === 'Job') name = 'Card'

      return this.$options.components[name] ? name : 'Status'
    },

    bindings () {
      return this.entity?.bindings
    },

    autoRefresh: {
      get () {
        return this.entity?.autoRefresh || 0
      },
      set (value) {
        this.entity && (this.entity.autoRefresh = value)
      }
    },

    autoRestoreSeconds: {
      get () {
        return this.entity?.autoRefresh || 0
      },
      set (value) {
        this.entity && (this.entity.autoRefresh = value)
      }
    },

    cache: vm => vm.entity.cache,
  },

  beforeMount () {
    if (!this.noFetch) this.restore()
  },

  mounted () {
    this.enableAutoRestore();
  },

  unmounted () {
    this.disableAutoRestore()
  },

  watch: {
    $route () {
      if (!this.noFetch) this.restore()
    },

    autoRestoreSeconds () {
      this.resetAutoRestore()
    },
  },

  methods: {

    hack () {
      this.$set(this.entity.cache, 'form/field', {value: Math.random() })
    },
    save () {
      this.$store.commit('entity/store', this.entity)
    },

    enableAutoRestore () {
      clearInterval(window.$timer)
      window.$timer = setInterval(this.autoRestore, 1000)
      this.resetAutoRestore()
    },

    disableAutoRestore () {
      this.resetAutoRestore()
      clearInterval(window.$timer)
    },

    resetAutoRestore () {
      let due = null
      if (this.autoRestoreSeconds && this.$refs.component.refresh) {
        due = new Date()
        due.setSeconds(due.getSeconds() + this.autoRestoreSeconds)
      }
      this.autoRestoreDate = due
    },

    autoRestore: async function () {
      if (!this.autoRestoreDate)
        return

      const refresh = this.$refs.component.refresh

      if (!refresh || this.busy || this.autoRestoreSeconds == 0){
        return this.resetAutoRestore()
      }

      if (this.autoRestoreDate.getTime() > new Date().getTime())
        return

      this.resetAutoRestore()

      this.$nextTick(() => { refresh() })
    },

    async restore(opts) {
      const { dataSetOnly } = (opts || {})
      if (dataSetOnly) {
        await this.restoreDataSet(opts)
      } else {
        await this.restoreEntity(opts)
      }
    },

    async restoreEntity(opts) {
      try {
        // Sincronizando a entidade...
        this.busy = true

        const { form, noCache, noStore } = (opts || {})

        // Quando se deseja apenas dados...
        const resolveOpts = { form, noCache, noStore: noStore }

        // Realizando a consulta...
        const status = await this.$resolve(this.entityLink, resolveOpts)

        const trigger = { site: this.site, eref: this.eref }
        if (!isCompatibleEref(trigger, status.entity))
          return

        const { entity } = status

        if (entity.see) {
          const { site, see : eref } = status.entity
          this.$router.push({ route: 'Workspace', params: { site, eref }})
          return
        }

        const rels = this.entity.rel || []
        const queryOnLoad = rels.some(rel => rel === 'QueryOnLoad')

        if (queryOnLoad) {
          await this.restoreDataSet(opts)
        }

      } finally {
        this.resetAutoRestore()
        this.busy = false
      }
    },

    async restoreDataSet(opts) {
      try {
        // Sincronizando a entidade...
        this.busy = true

        const trigger = { site: this.site, eref: this.eref }

        const dataSet = this.entity?.dataSet
        let dataSetEref = dataSet?.see || dataSet?.eref;

        //
        // MONTANDO UM EREF DE DATASET PADRÃO BASEADO NA ROTA ATUAL
        //
        if (!dataSetEref) {
          // Usando expressão regular para extrair a parte antes de '(' e os argumentos entre parênteses
          const erefRegex = /^([^\(]+)(?:\(([^)]*)\))?/;
          const matches = this.entity.eref.match(erefRegex);
          // matches[1] contém a parte antes de '(', e matches[2] contém os argumentos, se houver
          const entityEref = matches[1];
          let entityArgs = ""; // Inicializa entityArgs como uma string vazia
          // Se houver argumentos, inclui os parênteses ao redor dos argumentos em entityArgs
          if (matches[2]) {
            entityArgs = `(${matches[2]})`;
          }
          dataSetEref = `${entityEref}.DataSet${entityArgs}`;
        }

        //
        // REPASSANDO PARÂMETROS DE ROTA SE NECESSÁRIO
        //
        if (dataSetEref.includes('=') && this.entity.sref?.includes('(')) {

          // Verifica se existe a parte de parâmetros em this.entity.sref
          const parameters = {};
          const paramPattern = /\((.*)\)$/;
          const matches = this.entity.sref.match(paramPattern);
          if (matches && matches[1]) {
            // Extrai a string de parâmetros
            const paramString = matches[1];
            // Separa os parâmetros individuais
            const params = paramString.split(';');
            params.forEach(param => {
              // Separa cada parâmetro em chave e valor
              const [key, value] = param.split('=');
              // Adiciona ao mapa de parâmetros apenas se o valor não for vazio
              if (value) {
                  parameters[key] = value;
              }
            });
          }

            // Extrai a base da Eref e os parâmetros existentes entre parênteses
          const [baseEref, paramString] = dataSetEref.split('(');
          // Inicializa um array para acumular os parâmetros atualizados
          let updatedParams = [];

          // Separa os parâmetros individuais pela presença do delimitador ';'
          const params = paramString?.slice(0, -1).split(';') || [];

          params.forEach(param => {
            // Separa o nome do parâmetro do seu valor
            const [key, value] = param.split('=');
            // Se o valor do parâmetro está vazio e existe no hash 'parameters', substitui pelo valor correspondente
            if (value === '' && parameters.hasOwnProperty(key)) {
              updatedParams.push(`${key}=${parameters[key]}`);
            } else {
              // Se o parâmetro já possui um valor ou não está presente em 'parameters', mantém como está
              updatedParams.push(param);
            }
          });

          // Reconstrói a string de parâmetros com os valores atualizados
          const updatedParamString = updatedParams.join(';');

          // Reconstrói a string 'dataSetEref' com os parâmetros atualizados
          dataSetEref = `${baseEref}(${updatedParamString});`;
        }

        const dataSetLink = {
          site: dataSet?.site,
          eref: dataSetEref,
          form: dataSet?.form,
          data: dataSet?.data
        }

        if (!dataSetLink.eref)
          return // Nada a fazer...

        const { form, noStore } = (opts || {})

        const resolveOpts = {
          form,
          noCache: true,
          noStore: true
        }

        // Realizando a consulta...
        const status = await this.$resolve(dataSetLink, resolveOpts)

        if (!status.ok
          || !isCompatibleEref(dataSetLink, status.entity)
          || !isCompatibleEref(this, trigger))
          return

        // Atualizando o dataSet da entidade atual...
        this.$set(this.entity, 'dataSet', status.entity)

        if (!noStore) {
          this.$store.commit(STORE_ENTITY, this.entity);
        }

      } finally {
        this.resetAutoRestore()
        this.busy = false
      }
    },

    setAutoRefresh(opts) {
      this.$set(this.entity, 'autoRefresh', opts.autoRefresh || 0)
    }
  },

  beforeRouteUpdate(to, from, next) {
    this.previousRoute = from.params
    next()
  },

  // Eventos:
  // beforeCreate  : () => console.log('beforeCreate '),
  // created       : () => console.log('created      '),
  // beforeMount   : () => console.log('beforeMount  '),
  // mounted       : () => console.log('mounted      '),
  // beforeUpdate  : () => console.log('beforeUpdate '),
  // updated       : () => console.log('updated      '),
  // beforeUnmount : () => console.log('beforeUnmount'),
  // unmounted     : () => console.log('unmounted    '),
  // beforeRouteUpdate(to, from, next) {
  //   next();
  // },
};
</script>